export const INVOICE_STATUS_FILTER_KEY = "invoiceStatus";

const INVOICED = "Invoiced";
const NOT_INVOICED = "Not Invoiced";

type FilterMap = Record<string, unknown> | undefined;

const toStrings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string")
    : [];

const withStatus = (map: FilterMap, extra: string[]) =>
  extra.length === 0
    ? map
    : { ...map, status: [...new Set([...toStrings(map?.status), ...extra])] };

const withoutInvoiceStatus = (map: FilterMap) => {
  if (!map) return map;
  const { [INVOICE_STATUS_FILTER_KEY]: _omit, ...rest } = map;
  return rest;
};

/**
 * "Invoiced" is not a separate backend field; it is the order status value "Invoiced".
 * Invoice Status selections are translated into status include/exclude values.
 */
export function applyInvoiceStatusFilter<T extends FilterMap>(
  include: T,
  exclude: T,
): { include: FilterMap; exclude: FilterMap } {
  const includedInvoice = toStrings(include?.[INVOICE_STATUS_FILTER_KEY]);
  const excludedInvoice = toStrings(exclude?.[INVOICE_STATUS_FILTER_KEY]);

  const resolve = (selected: string[]) => {
    const hasInvoiced = selected.includes(INVOICED);
    const hasNotInvoiced = selected.includes(NOT_INVOICED);
    if (hasInvoiced === hasNotInvoiced) return undefined;
    return hasInvoiced ? "invoiced" : "notInvoiced";
  };

  const includeSide = resolve(includedInvoice);
  const excludeSide = resolve(excludedInvoice);

  const toInclude: string[] = [];
  const toExclude: string[] = [];
  if (includeSide === "invoiced") toInclude.push(INVOICED);
  if (includeSide === "notInvoiced") toExclude.push(INVOICED);
  if (excludeSide === "invoiced") toExclude.push(INVOICED);
  if (excludeSide === "notInvoiced") toInclude.push(INVOICED);

  // Excluding both options excludes every order; including and excluding the same status matches none.
  if (
    excludedInvoice.includes(INVOICED) &&
    excludedInvoice.includes(NOT_INVOICED)
  ) {
    toInclude.push(INVOICED);
    toExclude.push(INVOICED);
  }

  return {
    include: withStatus(withoutInvoiceStatus(include), toInclude),
    exclude: withStatus(withoutInvoiceStatus(exclude), toExclude),
  };
}
